// src/v3/ui/_common/components/ui/symbol-combobox.tsx
//
// Async search combobox for Symbol selection.
// Replaces the platform dropdown with 500+ searchable options in portfolio modals.
//
// Fetches from GET /api/symbols/search?q=...&limit=20 — lightweight payload
// (id, symbol, name, currency only, no JSONB fields).
//
// Keyboard: ArrowUp/Down to navigate, Enter to select, Escape to close.

import { ChevronDown, Loader2, Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../lib/http-client";
import { cn } from "../../lib/utils";

interface SymbolSearchResult {
  id: string;
  symbol: string;
  name: string | null;
  currency: string | null;
}

export interface SymbolComboboxProps {
  value: string;
  onChange: (symbolId: string) => void;
  /** Called with the full result when a symbol is selected (includes currency). */
  onSelectItem?: (item: SymbolSearchResult) => void;
  placeholder?: string;
  className?: string;
  /** When provided, displays this label instead of fetching by id. */
  initialLabel?: string | undefined;
}

const inputClass =
  "flex w-full items-center gap-2 rounded-md border border-input bg-background text-sm text-foreground transition-colors focus-within:ring-2 focus-within:ring-ring h-9 px-3";

export function SymbolCombobox({
  value,
  onChange,
  onSelectItem,
  placeholder = "Search symbol by ticker or name...",
  className,
  initialLabel,
}: SymbolComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SymbolSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(
    initialLabel ?? null,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // Update selected label when value changes externally (e.g. edit mode)
  useEffect(() => {
    if (value && !selectedLabel) {
      // Try to find in current results first
      const found = results.find((r) => r.id === value);
      if (found) {
        setSelectedLabel(formatLabel(found));
      }
    }
    if (!value) {
      setSelectedLabel(null);
    }
  }, [value, results, selectedLabel]);

  const positionPanel = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < panelHeight + 8 && rect.top > panelHeight + 8;
    setPanelPos({
      top: above ? rect.top - panelHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel, results]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open, positionPanel]);

  // Debounced search
  const doSearch = useCallback((q: string) => {
    const reqId = ++requestIdRef.current;
    if (q.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiFetch<{ items: SymbolSearchResult[] }>(
      `/api/symbols/search?q=${encodeURIComponent(q)}&limit=20`,
    )
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        setResults(res.items);
        setHighlightIndex(res.items.length > 0 ? 0 : -1);
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        setResults([]);
        setHighlightIndex(-1);
      })
      .finally(() => {
        if (reqId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, []);

  const handleQueryChange = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const openDropdown = useCallback(() => {
    setOpen(true);
    // Trigger initial search if there's already a query
    if (query.trim().length > 0) {
      doSearch(query);
    }
  }, [query, doSearch]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setHighlightIndex(-1);
  }, []);

  const selectOption = useCallback(
    (idx: number) => {
      const opt = results[idx];
      if (!opt) return;
      onChange(opt.id);
      onSelectItem?.(opt);
      setSelectedLabel(formatLabel(opt));
      setQuery("");
      setResults([]);
      closeDropdown();
      inputRef.current?.focus();
    },
    [results, onChange, onSelectItem, closeDropdown],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (
        containerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      )
        return;
      closeDropdown();
    };
    document.addEventListener("pointerdown", handlePointer);
    return () => document.removeEventListener("pointerdown", handlePointer);
  }, [open, closeDropdown]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeDropdown();
        break;
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightIndex >= 0) selectOption(highlightIndex);
        break;
      case "Tab":
        closeDropdown();
        break;
    }
  };

  const displayValue = open
    ? query
    : (selectedLabel ?? "");

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        className={inputClass}
        onClick={() => {
          if (!open) openDropdown();
          inputRef.current?.focus();
        }}
        onKeyDown={handleKeyDown}
      >
        {!open && selectedLabel ? (
          <span className="flex-1 truncate text-foreground">
            {selectedLabel}
          </span>
        ) : (
          <>
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={displayValue}
              onChange={(e) => {
                if (!open) setOpen(true);
                handleQueryChange(e.target.value);
              }}
              onFocus={openDropdown}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              aria-label="Search symbol"
            />
          </>
        )}
        {loading && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        )}
        {!loading && (
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        )}
      </div>
      {open &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            aria-label="Symbol results"
            style={{
              position: "fixed",
              top: panelPos.top,
              left: panelPos.left,
              width: panelPos.width,
              zIndex: 50,
            }}
            className="max-h-60 overflow-y-auto rounded-md border border-input bg-background py-1 shadow-md"
            onKeyDown={handleKeyDown}
          >
            {loading && results.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Searching...
              </div>
            )}
            {!loading && results.length === 0 && query.trim().length > 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No symbols found
              </div>
            )}
            {!loading && results.length === 0 && query.trim().length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Type to search...
              </div>
            )}
            {results.map((opt, idx) => (
              <div
                key={opt.id}
                role="option"
                aria-selected={opt.id === value}
                onClick={() => selectOption(idx)}
                onPointerEnter={() => setHighlightIndex(idx)}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm text-foreground",
                  idx === highlightIndex && "bg-accent text-accent-foreground",
                  opt.id === value && idx !== highlightIndex && "bg-accent/50",
                )}
              >
                <span className="font-medium">{opt.symbol}</span>
                {opt.name && (
                  <span className="truncate text-muted-foreground">
                    {opt.name}
                  </span>
                )}
                {opt.currency && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {opt.currency}
                  </span>
                )}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function formatLabel(s: SymbolSearchResult): string {
  return s.name ? `${s.symbol} — ${s.name}` : s.symbol;
}
